import { gsap } from "gsap";

import { AbstractBitmapText } from "../../../../abstracts/graphics/AbstractBitmapText";
import { AbstractSpine } from "../../../../abstracts/graphics/AbstractSpine";
import { playSound, playSoundOnce } from "../../../../abstracts/utils/SoundUtils";
import { fishMultipliersMap, symbolConfig } from "../../../UCConfig";
import { UCFontsNames, UCSoundNames, UCSpineNames } from "../../../assetsLoader/UCAssetsNames";
import { UCSpecialSymbolNames, UCSymbolNames } from "../../../types/UCEnums";
import { UCSymbol } from "./UCSymbol";
import { symbolsResizeConfig } from "./UCSymbolsResizeConfig";

export class UCWildSymbol extends UCSymbol {
    protected multiplierText: AbstractBitmapText;
    protected multiplier: { value: number } = { value: 0 };
    public isGold: boolean = false;

    constructor() {
        super(UCSymbolNames.WILD);
    }

    protected generateSymbol(): void {
        this.mainSpine = new AbstractSpine(UCSpineNames.SILVER_WILD);
        this.mainSpine.getSpineTween(`Normal`);

        this.alignSymbol();
        this.addChild(this.mainSpine);

        this.multiplierText = new AbstractBitmapText(`x0`, {
            fontName: UCFontsNames.UC_MULTIPLIER,
            fontSize: 44,
        });
        this.multiplierText.name = `multiplierText`;
        this.multiplierText.position.set(0, 1);
        this.multiplierText.rotation = -0.07;
    }

    private alignSymbol(): void {
        this.mainSpine.x =
            symbolConfig.symbolWidth / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
        this.mainSpine.y =
            symbolConfig.symbolHeight / 2 +
            symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;
    }

    public changeToGold(): void {
        this.removeChild(this.mainSpine);

        this.mainSpine = new AbstractSpine(UCSpineNames.GOLD_WILD);
        this.mainSpine.getSpineTween(`Normal`);

        this.alignSymbol();
        this.addChild(this.mainSpine);

        this.isGold = true;
    }

    public getActivationAnimation(): gsap.core.Tween | gsap.core.Timeline {
        this.isActivated = true;
        this.multiplierText.updateValue(`x1`);

        return this.mainSpine.getSpineTween(`Board_collect_transform`);
    }

    public updateMultiplier(fishName: UCSpecialSymbolNames): gsap.core.Timeline {
        const newValue = fishMultipliersMap.get(fishName)!;
        const fishId: number = +fishName.replace(/^\D+/g, ``);

        const initialValue = this.multiplier.value;
        const finalValue = initialValue + newValue;
        const newMultiplier = { value: initialValue };
        const duration = 0.2 + fishId * 0.1;
        const timeline = gsap.timeline({});

        this.multiplier.value = finalValue;

        let soundCounter = newMultiplier.value;
        const updateMultiplierTween = gsap.to(newMultiplier, {
            value: newMultiplier.value + newValue,
            duration: duration,
            onUpdate: () => {
                if (soundCounter <= newMultiplier.value - 1) {
                    playSound({
                        name: UCSoundNames.WILD_GOLD_COUNTER,
                    });
                    soundCounter = newMultiplier.value;
                }
                this.multiplierText.updateValue(`x${Math.round(newMultiplier.value)}`);
            },
            onStart: () => {
                this.mainSpine.addChild(this.multiplierText);
            },
            onComplete: () => {
                playSoundOnce({
                    name: UCSoundNames.WILD_GOLD_COLLECT_END,
                    playOnceMinDuration: 1,
                });
            },
        });

        timeline.add(updateMultiplierTween, 0);

        if (fishId >= 4) {
            const cloneText = new AbstractBitmapText(
                `x${Math.round(newMultiplier.value + newValue)}`,
                {
                    fontName: UCFontsNames.UC_MULTIPLIER,
                    fontSize: 44,
                }
            );
            cloneText.scale.set(this.multiplierText.scale.x);
            cloneText.position.set(this.multiplierText.x, this.multiplierText.y);
            cloneText.rotation = this.multiplierText.rotation;

            const highlightCloneTween = gsap.to(cloneText, {
                pixi: { scale: `+=0.2`, tint: 0xffff00, y: `-=5` },
                duration: 0.15,
                ease: `power1.in`,
                onStart: () => {
                    cloneText.cacheAsBitmap = false;
                    this.mainSpine.addChild(cloneText);
                    this.multiplierText.alpha = 0;
                },
            });
            timeline.add(highlightCloneTween);

            const hideCloneTween = gsap.to(cloneText, {
                pixi: { scale: `+=0.3`, y: `-=20` },
                alpha: 0,
                duration: 0.2,
                ease: `power1.out`,
                onComplete: () => {
                    this.mainSpine.removeChild(cloneText);
                    cloneText.destroy();
                },
            });
            timeline.add(hideCloneTween);

            const showMultiplierTween = gsap.to(this.multiplierText, {
                alpha: 1,
                duration: 0.15,
                ease: `power1.out`,
            });
            timeline.add(showMultiplierTween, `<`);
        }

        return timeline;
    }

    public updateMultiplierFromBoat(multiplier: number): gsap.core.Timeline {
        const initialValue = this.multiplier.value;
        const finalValue = initialValue * multiplier;
        const newMultiplier = { value: initialValue };
        let duration = 0.3;
        let soundCounter = newMultiplier.value;

        if (multiplier === 3) {
            duration = 0.4;
        } else if (multiplier === 10) {
            duration = 0.5;
        }
        const timeline = gsap.timeline();

        this.multiplier.value = finalValue;

        const updateMultiplierTween = gsap.to(newMultiplier, {
            value: newMultiplier.value * multiplier,
            duration: duration,
            onUpdate: () => {
                if (soundCounter <= newMultiplier.value - 1) {
                    playSound({
                        name: UCSoundNames.WILD_GOLD_COUNTER,
                    });
                    soundCounter = newMultiplier.value;
                }
                this.multiplierText.updateValue(`x${Math.round(newMultiplier.value)}`);
            },
            onStart: () => {
                this.mainSpine.addChild(this.multiplierText);
            },
            onComplete: () => {
                playSoundOnce({
                    name: UCSoundNames.WILD_GOLD_COLLECT_END,
                    playOnceMinDuration: 1,
                });
            },
        });

        timeline.add(updateMultiplierTween, 0);

        const cloneText = new AbstractBitmapText(
            `x${Math.round(newMultiplier.value * multiplier)}`,
            {
                fontName: UCFontsNames.UC_MULTIPLIER,
                fontSize: 44,
            }
        );
        cloneText.scale.set(this.multiplierText.scale.x);
        cloneText.position.set(this.multiplierText.x, this.multiplierText.y);
        cloneText.rotation = this.multiplierText.rotation;

        const highlightCloneTween = gsap.to(cloneText, {
            pixi: { scale: `+=0.2`, tint: 0xff0000, y: `-=5` },
            duration: 0.15,
            ease: `power1.in`,
            onStart: () => {
                cloneText.cacheAsBitmap = false;
                this.mainSpine.addChild(cloneText);
                this.multiplierText.alpha = 0;
            },
        });
        timeline.add(highlightCloneTween);

        const hideCloneTween = gsap.to(cloneText, {
            pixi: { scale: `+=0.3`, y: `-=20` },
            alpha: 0,
            duration: 0.2,
            ease: `power1.out`,
            onComplete: () => {
                this.mainSpine.removeChild(cloneText);
                cloneText.destroy();
            },
        });
        timeline.add(hideCloneTween);

        const showMultiplierTween = gsap.to(this.multiplierText, {
            alpha: 1,
            duration: 0.15,
            ease: `power1.out`,
        });
        timeline.add(showMultiplierTween, `<`);

        return timeline;
    }

    public override getWinAnimation(): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline({
            onStart: () => {
                //@ts-ignore
                this.mainSpine.filters = null;
                this.mainSpine.tint = 0xffffff;
            },
        });
        let symbolAnimation: gsap.core.Tween = this.mainSpine.getSpineTween(
            this.isActivated ? `MultiWin` : `Win`
        );
        tl.add(symbolAnimation, 0);

        return tl;
    }

    protected override resetMainSpine(isSpin: boolean = false) {
        if (this.mainSpine !== undefined) {
            if (!isSpin) {
                this.multiplier.value = 0;
                this.mainSpine.removeChild(this.multiplierText);

                this.removeChild(this.mainSpine);

                this.mainSpine = new AbstractSpine(UCSpineNames.SILVER_WILD);
                this.mainSpine.getSpineTween(`Normal`);

                this.alignSymbol();

                this.mainSpine.mask = null;
                this.mainSpine.scale.set(0.5);

                this.mainSpine.x =
                    symbolConfig.symbolWidth / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetX!;
                this.mainSpine.y =
                    symbolConfig.symbolHeight / 2 +
                    symbolsResizeConfig.get(this.name as UCSymbolNames)!.default.offsetY!;

                this.isActivated = false;
            } else {
                if (this.isActivated) {
                    const revertToNoWinAnimation =
                        this.mainSpine.getSpineTween(`Board_collect_transform`);

                    revertToNoWinAnimation.totalProgress(0.99);
                } else {
                    this.mainSpine.reset();
                }
            }

            this.addChild(this.mainSpine);
        }
    }

    public override reset() {
        super.reset();

        this.isGold = false;
    }
}
